import {
  Application,
  Assets,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import { RaidRoomOption, RaidRoomConfig, RaidBossConfig } from "types/raids/rooms";
import { PlayerRole } from "types/raid-dynamodb";
import { RaidLobbyPlayer } from "@/providers/raid-provider";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { BattlePokemonData, Direction, PokemonType } from "types/battle-engine/battle";
import { gridTileSize } from "types/battle-engine/constants";

/**
 * Direction positions for the 3 room options
 * Players walk toward these positions when they vote
 */
export enum RoomDirection {
  TOP_LEFT = 0,
  TOP = 1,
  TOP_RIGHT = 2,
}

interface PlayerPokemonState {
  pokemon: ClientPokemon;
  playerRole: PlayerRole;
  currentVote: number | null;
  targetPosition: { x: number; y: number };
  basePosition: { x: number; y: number };
  isMoving: boolean;
  moveQueue: { x: number; y: number }[];
}

interface DirectionIndicator {
  container: Container;
  roomIndex: number;
  position: { x: number; y: number };
  voteCount: number;
}

const STEP_DURATION_MS = 250; // Duration for one grid tile step
const IDLE_BOUNCE_AMPLITUDE = 2;
const IDLE_BOUNCE_SPEED = 0.003;

/**
 * Immersive PIXI-based room selection scene.
 * Shows a single crossroads scene where player Pokemon walk to vote for directions.
 */
export class RoomSelectionSceneV2 {
  private app: Application;
  private world: Container;
  private isDestroyed = false;

  // Scene dimensions
  private screenWidth: number;
  private screenHeight: number;

  // Background
  private backgroundSprite: Sprite | null = null;
  private fogContainer: Container;

  // Direction indicators (the 3 path options)
  private directionIndicators: DirectionIndicator[] = [];
  private directionContainer: Container;

  // Player pokemon
  private playerPokemonStates: Map<PlayerRole, PlayerPokemonState> = new Map();
  private pokemonContainer: Container;

  // Room options data
  private roomOptions: RaidRoomOption[] = [];

  // Animation frame ID for idle animation
  private idleAnimationFrame: number | null = null;

  constructor(app: Application, world: Container) {
    this.app = app;
    this.world = world;
    this.screenWidth = app.screen.width;
    this.screenHeight = app.screen.height;

    // Create containers
    this.fogContainer = new Container();
    this.fogContainer.zIndex = -500;
    this.world.addChild(this.fogContainer);

    this.directionContainer = new Container();
    this.directionContainer.zIndex = 100;
    this.world.addChild(this.directionContainer);

    this.pokemonContainer = new Container();
    this.pokemonContainer.sortableChildren = true;
    this.pokemonContainer.zIndex = 200;
    this.world.addChild(this.pokemonContainer);
  }

  /**
   * Initialize the scene with room options and players
   */
  async initialize(
    roomOptions: RaidRoomOption[],
    players: Record<PlayerRole, RaidLobbyPlayer | null>,
  ): Promise<void> {
    if (this.isDestroyed) return;

    this.roomOptions = roomOptions.slice(0, 3);
    this.screenWidth = this.app.screen.width;
    this.screenHeight = this.app.screen.height;

    // Load and setup background
    await this.setupBackground();

    // Create direction indicators
    this.createDirectionIndicators();

    // Spawn player pokemon
    await this.spawnPlayerPokemon(players);

    // Start idle animation loop
    this.startIdleAnimation();
  }

  /**
   * Setup the crossroads background
   */
  private async setupBackground(): Promise<void> {
    if (this.isDestroyed) return;

    // Draw a gradient background representing a misty forest crossroads
    const bg = new Graphics();
    
    // Base gradient - dark forest floor
    bg.rect(0, 0, this.screenWidth, this.screenHeight);
    bg.fill({ color: 0x1a2634 });
    
    // Add some atmosphere - lighter area toward the paths
    const gradient = new Graphics();
    gradient.ellipse(this.screenWidth / 2, this.screenHeight * 0.3, this.screenWidth * 0.6, this.screenHeight * 0.4);
    gradient.fill({ color: 0x2d4a5e, alpha: 0.5 });
    
    this.world.addChildAt(bg, 0);
    this.world.addChildAt(gradient, 1);

    // Draw path lines converging from bottom center to three directions
    const paths = new Graphics();
    const bottomCenter = { x: this.screenWidth / 2, y: this.screenHeight * 0.85 };
    const forkPoint = { x: this.screenWidth / 2, y: this.screenHeight * 0.45 };

    // Main path from bottom to fork
    paths.moveTo(bottomCenter.x - 30, bottomCenter.y);
    paths.lineTo(forkPoint.x - 15, forkPoint.y + 20);
    paths.lineTo(forkPoint.x + 15, forkPoint.y + 20);
    paths.lineTo(bottomCenter.x + 30, bottomCenter.y);
    paths.closePath();
    paths.fill({ color: 0x3d5a6e, alpha: 0.6 });

    // Left path
    const leftEnd = this.getDirectionPosition(RoomDirection.TOP_LEFT);
    paths.moveTo(forkPoint.x - 15, forkPoint.y + 20);
    paths.lineTo(leftEnd.x - 20, leftEnd.y + 40);
    paths.lineTo(leftEnd.x + 20, leftEnd.y + 40);
    paths.lineTo(forkPoint.x, forkPoint.y + 10);
    paths.closePath();
    paths.fill({ color: 0x3d5a6e, alpha: 0.5 });

    // Center path
    const centerEnd = this.getDirectionPosition(RoomDirection.TOP);
    paths.moveTo(forkPoint.x - 10, forkPoint.y + 10);
    paths.lineTo(centerEnd.x - 25, centerEnd.y + 40);
    paths.lineTo(centerEnd.x + 25, centerEnd.y + 40);
    paths.lineTo(forkPoint.x + 10, forkPoint.y + 10);
    paths.closePath();
    paths.fill({ color: 0x4a6b7f, alpha: 0.6 });

    // Right path
    const rightEnd = this.getDirectionPosition(RoomDirection.TOP_RIGHT);
    paths.moveTo(forkPoint.x, forkPoint.y + 10);
    paths.lineTo(rightEnd.x - 20, rightEnd.y + 40);
    paths.lineTo(rightEnd.x + 20, rightEnd.y + 40);
    paths.lineTo(forkPoint.x + 15, forkPoint.y + 20);
    paths.closePath();
    paths.fill({ color: 0x3d5a6e, alpha: 0.5 });

    paths.zIndex = -100;
    this.world.addChild(paths);

    // Add fog/mist particles
    this.createFogParticles();
  }

  /**
   * Create ambient fog particles
   */
  private createFogParticles(): void {
    for (let i = 0; i < 8; i++) {
      const fog = new Graphics();
      fog.ellipse(0, 0, 40 + Math.random() * 60, 15 + Math.random() * 20);
      fog.fill({ color: 0xffffff, alpha: 0.03 + Math.random() * 0.05 });
      
      fog.x = Math.random() * this.screenWidth;
      fog.y = this.screenHeight * (0.2 + Math.random() * 0.5);
      
      this.fogContainer.addChild(fog);

      // Animate fog drift
      const speed = 0.1 + Math.random() * 0.2;
      const direction = Math.random() > 0.5 ? 1 : -1;
      
      const animateFog = () => {
        if (this.isDestroyed || fog.destroyed) return;
        fog.x += speed * direction;
        if (fog.x > this.screenWidth + 100) fog.x = -100;
        if (fog.x < -100) fog.x = this.screenWidth + 100;
        requestAnimationFrame(animateFog);
      };
      requestAnimationFrame(animateFog);
    }
  }

  /**
   * Get screen position for a direction
   */
  private getDirectionPosition(direction: RoomDirection): { x: number; y: number } {
    const topY = this.screenHeight * 0.12;
    
    switch (direction) {
      case RoomDirection.TOP_LEFT:
        return { x: this.screenWidth * 0.18, y: topY + 30 };
      case RoomDirection.TOP:
        return { x: this.screenWidth * 0.5, y: topY };
      case RoomDirection.TOP_RIGHT:
        return { x: this.screenWidth * 0.82, y: topY + 30 };
    }
  }

  /**
   * Create the direction indicator UI for each path
   */
  private createDirectionIndicators(): void {
    for (let i = 0; i < Math.min(3, this.roomOptions.length); i++) {
      const room = this.roomOptions[i];
      const position = this.getDirectionPosition(i as RoomDirection);
      const indicator = this.createSingleIndicator(room, i, position);
      
      this.directionIndicators.push({
        container: indicator,
        roomIndex: i,
        position,
        voteCount: 0,
      });
      
      this.directionContainer.addChild(indicator);
    }
  }

  /**
   * Create a single direction indicator
   */
  private createSingleIndicator(
    room: RaidRoomOption,
    index: number,
    position: { x: number; y: number },
  ): Container {
    const container = new Container();
    container.x = position.x;
    container.y = position.y;

    // Glow effect behind
    const glow = new Graphics();
    glow.ellipse(0, 30, 50, 20);
    glow.fill({ color: this.getTypeColor(room.mainType), alpha: 0.3 });
    container.addChild(glow);

    // Direction arrow/sign
    const sign = new Graphics();
    // Arrow shape pointing up
    sign.moveTo(0, -25);
    sign.lineTo(-18, 10);
    sign.lineTo(-8, 10);
    sign.lineTo(-8, 30);
    sign.lineTo(8, 30);
    sign.lineTo(8, 10);
    sign.lineTo(18, 10);
    sign.closePath();
    sign.fill({ color: this.getTypeColor(room.mainType), alpha: 0.85 });
    sign.stroke({ color: 0xffffff, width: 2, alpha: 0.9 });
    container.addChild(sign);

    // Room number text
    const numberText = new Text({
      text: `${index + 1}`,
      style: {
        fontFamily: "vck",
        fontSize: 18,
        fill: 0xffffff,
        fontWeight: "bold",
      },
    });
    numberText.anchor.set(0.5);
    numberText.y = 5;
    container.addChild(numberText);

    // Type badge below
    const typeText = new Text({
      text: room.mainType.toUpperCase(),
      style: {
        fontFamily: "vck",
        fontSize: 10,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
      },
    });
    typeText.anchor.set(0.5);
    typeText.y = 45;
    container.addChild(typeText);

    return container;
  }

  /**
   * Get color for Pokemon type
   */
  private getTypeColor(type: string): number {
    const typeColors: Record<string, number> = {
      normal: 0xa8a878,
      fire: 0xf08030,
      water: 0x6890f0,
      electric: 0xf8d030,
      grass: 0x78c850,
      ice: 0x98d8d8,
      fighting: 0xc03028,
      poison: 0xa040a0,
      ground: 0xe0c068,
      flying: 0xa890f0,
      psychic: 0xf85888,
      bug: 0xa8b820,
      rock: 0xb8a038,
      ghost: 0x705898,
      dragon: 0x7038f8,
      dark: 0x705848,
      steel: 0xb8b8d0,
      fairy: 0xee99ac,
    };
    return typeColors[type.toLowerCase()] || 0x888888;
  }

  /**
   * Spawn player Pokemon at the bottom of the screen
   */
  private async spawnPlayerPokemon(
    players: Record<PlayerRole, RaidLobbyPlayer | null>,
  ): Promise<void> {
    if (this.isDestroyed) return;

    const activeRoles = (Object.entries(players) as [PlayerRole, RaidLobbyPlayer | null][])
      .filter(([_, player]) => player !== null)
      .map(([role, player]) => ({ role, player: player! }));

    const spacing = 70;
    const totalWidth = (activeRoles.length - 1) * spacing;
    const startX = this.screenWidth / 2 - totalWidth / 2;
    const baseY = this.screenHeight * 0.75;

    const spawnPromises = activeRoles.map(async ({ role, player }, index) => {
      if (this.isDestroyed) return;
      if (!player.team_preview || player.team_preview.length === 0) return;

      const firstPokemon = player.team_preview[0];
      const position = { x: startX + index * spacing, y: baseY };

      try {
        const battleData: BattlePokemonData = {
          id: 70000 + index,
          base_pokemon_name: firstPokemon.base_pokemon_name,
          name: firstPokemon.base_pokemon_name,
          level: firstPokemon.level,
          pokemon_types: [PokemonType.NORMAL],
          hp: 100,
          max_hp: 100,
          ability_power: 10,
          resistance: 10,
          speed: 10,
          skills: [],
          scale: 1,
        };

        const pokemon = new ClientPokemon(
          battleData,
          { x: 0, y: 0 },
          "A",
          this.app,
          this.pokemonContainer,
          true, // hideUI
          true, // hideHPBar
        );

        await pokemon.load();

        if (this.isDestroyed) {
          (pokemon as any).container?.destroy({ children: true });
          return;
        }

        // Position manually (not using grid coordinates)
        const pokemonContainer = (pokemon as any).container as Container;
        if (pokemonContainer) {
          pokemonContainer.x = position.x;
          pokemonContainer.y = position.y;
          pokemonContainer.zIndex = position.y;
        }

        // Face north initially
        pokemon.setAnimation(["Idle"], Direction.N);

        // Add player name label
        const nameLabel = new Text({
          text: player.username,
          style: {
            fontFamily: "vck",
            fontSize: 11,
            fill: 0xffffff,
            stroke: { color: 0x000000, width: 4 },
          },
        });
        nameLabel.anchor.set(0.5);
        nameLabel.y = -45;
        pokemonContainer.addChild(nameLabel);

        this.playerPokemonStates.set(role, {
          pokemon,
          playerRole: role,
          currentVote: null,
          targetPosition: position,
          basePosition: { ...position },
          isMoving: false,
          moveQueue: [],
        });
      } catch {
        // Failed to spawn pokemon
      }
    });

    await Promise.all(spawnPromises);
  }

  /**
   * Update votes and move Pokemon toward voted direction
   */
  updateVotes(votes: Record<PlayerRole, number>): void {
    if (this.isDestroyed) return;

    // Reset vote counts
    this.directionIndicators.forEach((ind) => (ind.voteCount = 0));

    // Process each vote
    for (const [role, roomIndex] of Object.entries(votes)) {
      const playerRole = role as PlayerRole;
      const state = this.playerPokemonStates.get(playerRole);
      if (!state) continue;

      // Count votes
      const indicator = this.directionIndicators.find((d) => d.roomIndex === roomIndex);
      if (indicator) indicator.voteCount++;

      // Move pokemon if vote changed
      if (state.currentVote !== roomIndex) {
        state.currentVote = roomIndex;
        this.movePokemonToDirection(state, roomIndex);
      }
    }

    // Move non-voters back to base position
    for (const [role, state] of this.playerPokemonStates) {
      if (votes[role] === undefined && state.currentVote !== null) {
        state.currentVote = null;
        this.movePokemonToBase(state);
      }
    }
  }

  /**
   * Animate pokemon walking toward a direction
   */
  private movePokemonToDirection(state: PlayerPokemonState, directionIndex: number): void {
    const direction = this.getDirectionPosition(directionIndex as RoomDirection);
    
    // Calculate a position partway between base and direction indicator
    const distanceRatio = 0.45;
    const targetX = state.basePosition.x + (direction.x - state.basePosition.x) * distanceRatio;
    const targetY = state.basePosition.y + (direction.y - state.basePosition.y) * distanceRatio;

    this.animatePokemonWalk(state, { x: targetX, y: targetY });
  }

  /**
   * Animate pokemon walking back to base position
   */
  private movePokemonToBase(state: PlayerPokemonState): void {
    this.animatePokemonWalk(state, state.basePosition);
  }

  /**
   * Animate pokemon walking to a target position using grid-like movement
   */
  private async animatePokemonWalk(
    state: PlayerPokemonState,
    target: { x: number; y: number },
  ): Promise<void> {
    state.targetPosition = target;
    if (state.isMoving) return;

    state.isMoving = true;

    while (!this.isDestroyed) {
      const pokemonContainer = (state.pokemon as any).container as Container;
      if (!pokemonContainer || pokemonContainer.destroyed) break;

      const currentX = pokemonContainer.x;
      const currentY = pokemonContainer.y;
      const targetX = state.targetPosition.x;
      const targetY = state.targetPosition.y;

      const dx = targetX - currentX;
      const dy = targetY - currentY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // If close enough to final target, snap and stop
      if (dist < 5) {
        pokemonContainer.x = targetX;
        pokemonContainer.y = targetY;
        const lastDir = (state.pokemon as any).currentDir || Direction.N;
        state.pokemon.setAnimation(["Idle"], lastDir);
        break;
      }

      // Determine best grid step direction (8 directions only)
      const stepDir = this.getBestGridDirection(dx, dy);
      const moveVec = this.directionToVector(stepDir);

      // Move one full tile, or the remaining distance if shorter
      const stepSize = Math.min(gridTileSize, dist);
      const nextX = currentX + moveVec.x * stepSize;
      const nextY = currentY + moveVec.y * stepSize;

      // Animate this single grid-step
      await this.performGridStep(state, stepDir, nextX, nextY);
    }

    state.isMoving = false;
  }

  /**
   * Perform a single grid-aligned step animation
   */
  private performGridStep(
    state: PlayerPokemonState,
    dir: Direction,
    nextX: number,
    nextY: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const pokemonContainer = (state.pokemon as any).container as Container;
      if (!pokemonContainer || pokemonContainer.destroyed) {
        resolve();
        return;
      }

      const startX = pokemonContainer.x;
      const startY = pokemonContainer.y;
      const startTime = performance.now();

      state.pokemon.setAnimation(["Walk"], dir);

      const animate = () => {
        if (this.isDestroyed || pokemonContainer.destroyed) {
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / STEP_DURATION_MS);

        // Linear movement for grid-like feel
        pokemonContainer.x = startX + (nextX - startX) * t;
        pokemonContainer.y = startY + (nextY - startY) * t;
        pokemonContainer.zIndex = pokemonContainer.y;

        if (t < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Find the best of 8 directions for a given vector
   */
  private getBestGridDirection(dx: number, dy: number): Direction {
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);

    if (angle >= -22.5 && angle < 22.5) return Direction.E;
    if (angle >= 22.5 && angle < 67.5) return Direction.SE;
    if (angle >= 67.5 && angle < 112.5) return Direction.S;
    if (angle >= 112.5 && angle < 157.5) return Direction.SW;
    if (angle >= 157.5 || angle < -157.5) return Direction.W;
    if (angle >= -157.5 && angle < -112.5) return Direction.NW;
    if (angle >= -112.5 && angle < -67.5) return Direction.N;
    if (angle >= -67.5 && angle < -22.5) return Direction.NE;

    return Direction.N;
  }

  /**
   * Convert Direction enum to unit vector
   */
  private directionToVector(dir: Direction): { x: number; y: number } {
    switch (dir) {
      case Direction.N:
        return { x: 0, y: -1 };
      case Direction.NE:
        return { x: 0.707, y: -0.707 };
      case Direction.E:
        return { x: 1, y: 0 };
      case Direction.SE:
        return { x: 0.707, y: 0.707 };
      case Direction.S:
        return { x: 0, y: 1 };
      case Direction.SW:
        return { x: -0.707, y: 0.707 };
      case Direction.W:
        return { x: -1, y: 0 };
      case Direction.NW:
        return { x: -0.707, y: -0.707 };
      default:
        return { x: 0, y: 0 };
    }
  }

  /**
   * Convert dx/dy vector to Direction enum
   */
  private vectorToDirection(dx: number, dy: number): Direction {
    return this.getBestGridDirection(dx, dy);
  }

  /**
   * Start subtle idle animation for indicators
   */
  private startIdleAnimation(): void {
    const animate = () => {
      if (this.isDestroyed) return;

      const time = performance.now();

      // Subtle bounce on direction indicators
      this.directionIndicators.forEach((indicator, i) => {
        const offset = Math.sin(time * IDLE_BOUNCE_SPEED + i * 0.5) * IDLE_BOUNCE_AMPLITUDE;
        indicator.container.y = indicator.position.y + offset;
      });

      this.idleAnimationFrame = requestAnimationFrame(animate);
    };

    this.idleAnimationFrame = requestAnimationFrame(animate);
  }

  /**
   * Get the direction indicators for the overlay to position elements
   */
  getDirectionPositions(): Array<{ roomIndex: number; x: number; y: number }> {
    return this.directionIndicators.map((ind) => ({
      roomIndex: ind.roomIndex,
      x: ind.position.x,
      y: ind.position.y,
    }));
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    if (this.idleAnimationFrame) {
      cancelAnimationFrame(this.idleAnimationFrame);
    }

    // Destroy player pokemon
    for (const [, state] of this.playerPokemonStates) {
      try {
        const container = (state.pokemon as any).container;
        if (container && !container.destroyed) {
          container.destroy({ children: true });
        }
      } catch {
        // Cleanup failed
      }
    }
    this.playerPokemonStates.clear();

    // Destroy containers
    if (this.fogContainer && !this.fogContainer.destroyed) {
      this.fogContainer.destroy({ children: true });
    }
    if (this.directionContainer && !this.directionContainer.destroyed) {
      this.directionContainer.destroy({ children: true });
    }
    if (this.pokemonContainer && !this.pokemonContainer.destroyed) {
      this.pokemonContainer.destroy({ children: true });
    }

    this.directionIndicators = [];
  }
}
